if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.lastButtonAction = null; // Add this line
        this.pendingNavAnnouncement = false;
        this.accessibilityInitialized = false;
        this.lightboxSlidesCount = 0;
        this.lightboxStatusElement = null;
        this.lightboxNavTriggered = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryTablet);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlTablet) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightboxSlidesCount = dataSource.length;

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            const slider = this.sliderInstance.slider;
            // Store the current lightbox index for syncing on close
            this.lastLightboxIndex = currIndex;
            // Sync gallery slider to match lightbox
            if (slider.realIndex !== currIndex && slider.params.loop) {
              slider.slideToLoop(currIndex, 100, false);
            } else if (slider.activeIndex !== currIndex && !slider.params.loop) {
              slider.slideTo(currIndex, 100, false);
            }
          }
          if (this.lightboxNavTriggered) {
            this.announceLightboxSlide();
            this.lightboxNavTriggered = false;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightboxNavTriggered = true;
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightboxNavTriggered = true;
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });
        this.lightbox.on('beforeOpen', () => {
          this.lightboxStatusElement = null;
          // Store the initial index when lightbox opens
          if (this.sliderInstance && this.sliderInstance.slider) {
            const slider = this.sliderInstance.slider;
            this.lastLightboxIndex = slider.params.loop ? slider.realIndex : slider.activeIndex;
          }
          // Set dialog attributes when PhotoSwipe opens
          const pswpElement = document.querySelector('.pswp');
          if (pswpElement) {
            pswpElement.setAttribute('role', 'dialog');
            pswpElement.setAttribute('aria-modal', 'true');
            // Use aria-label directly - simpler and avoids extra announcements
            pswpElement.setAttribute('aria-label', 'Zoomed product image dialog');
            // Remove aria-labelledby if it exists to avoid conflicts
            pswpElement.removeAttribute('aria-labelledby');
          }
        });

        this.lightbox.on('afterInit', () => {
          // Ensure dialog attributes are set after initialization
          const pswpElement = document.querySelector('.pswp');
          if (pswpElement) {
            // Remove any label elements that might cause extra announcements
            const dialogLabel = pswpElement.querySelector('.pswp__dialog-label');
            if (dialogLabel) {
              dialogLabel.remove();
            }
            // Ensure aria-label is set and remove aria-labelledby
            if (!pswpElement.getAttribute('aria-label')) {
              pswpElement.setAttribute('aria-label', 'Zoomed product image dialog');
            }
            pswpElement.removeAttribute('aria-labelledby');
          }
          // Store the initial lightbox index after initialization
          if (this.lightbox?.pswp) {
            this.lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
          this.ensureLightboxStatusElement();
          this.announceLightboxSlide(true);
        });

        this.lightbox.on('close', () => {
          this.lightboxStatusElement = null;
          this.lightboxNavTriggered = false;
          
          // Sync gallery slider to the last viewed slide in lightbox
          if (this.sliderInstance && this.sliderInstance.slider && this.lightbox?.pswp) {
            const currIndex = this.lightbox.pswp.currIndex;
            const slider = this.sliderInstance.slider;
            
            // Use the stored index if available, otherwise use current
            const targetIndex = this.lastLightboxIndex !== undefined ? this.lastLightboxIndex : currIndex;
            
            // Sync gallery to match the last viewed slide
            if (slider.params.loop) {
              slider.slideToLoop(targetIndex, 300, false);
            } else {
              // Ensure index is within bounds
              const maxIndex = slider.slides.length - 1;
              const safeIndex = Math.min(Math.max(0, targetIndex), maxIndex);
              slider.slideTo(safeIndex, 300, false);
            }
            
            // Wait for slide transition to complete, then focus the correct element
            setTimeout(() => {
              // Update accessibility after slide change
              if (FoxTheme.config.mqlTablet) {
                this.updateSlideAccessibility();
                this.updateNavButtonsAccessibility();
              }
              
              // Focus the zoom button on the active slide
              const activeSlide = slider.slides[slider.activeIndex];
              if (activeSlide) {
                const mediaItem = activeSlide.classList.contains('product__media-item') 
                  ? activeSlide 
                  : activeSlide.querySelector('.product__media-item');
                
                if (mediaItem) {
                  // Ensure media item is accessible
                  mediaItem.removeAttribute('aria-hidden');
                  
                  const zoomButton = mediaItem.querySelector('.product__media-zoom');
                  if (zoomButton && mediaItem.dataset.mediaType === 'image') {
                    // Ensure zoom button is focusable
                    zoomButton.removeAttribute('aria-hidden');
                    zoomButton.removeAttribute('tabindex');
                    zoomButton.setAttribute('tabindex', '0');
                    
                    // Use double requestAnimationFrame to ensure all DOM updates are complete
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        try {
                          zoomButton.focus();
                        } catch (e) {
                          // Fallback if focus fails
                          console.warn('Could not focus zoom button:', e);
                        }
                      });
                    });
                  } else {
                    // If no zoom button or not an image, make media item focusable and focus it
                    mediaItem.setAttribute('tabindex', '0');
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        try {
                          mediaItem.focus();
                        } catch (e) {
                          // Fallback if focus fails
                          console.warn('Could not focus media item:', e);
                        }
                      });
                    });
                  }
                }
              }
            }, 400);
          }
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;
      
          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
      
          // Set initial accessibility state for mobile
          this.updateSlideAccessibility();
          this.updateNavButtonsAccessibility();
          
          // Track button clicks - add these 8 lines
          const prevButton = this.querySelector('.swiper-button-prev');
          const nextButton = this.querySelector('.swiper-button-next');
          if (prevButton)
            prevButton.addEventListener('click', () => {
              // this.lastButtonAction = 'Previous image';
              this.pendingNavAnnouncement = true;
            });
          if (nextButton)
            nextButton.addEventListener('click', () => {
              // this.lastButtonAction = 'Next image';
              this.pendingNavAnnouncement = true;
            });
        });
      }

      handleSlideChange() {

        const statusElement = this.querySelector('[id^="GalleryStatus"]');
  
        // Announce button action when transition starts
        this.sliderInstance.slider.on('slideChangeTransitionStart', (swiper) => {
          if (FoxTheme.config.mqlTablet && statusElement && this.lastButtonAction) {
            statusElement.textContent = this.lastButtonAction;
            this.lastButtonAction = null; // Clear after use
          }
        });
  
        // Use slideChangeTransitionEnd to ensure the active class is updated
        this.sliderInstance.slider.on('slideChangeTransitionEnd', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;
      
          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }
      
          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);
      
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
          
          // Update accessibility for mobile devices after transition completes
          // Use setTimeout to ensure DOM has fully updated
          setTimeout(() => {
            this.updateSlideAccessibility();
              this.updateNavButtonsAccessibility();
            if (this.pendingNavAnnouncement) {
              this.announceCurrentSlide();
              this.pendingNavAnnouncement = false;
            }
          }, 100);
        });
        
        // Keep realIndexChange for other functionality if needed
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;
      
          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }
      
          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);
      
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      updateSlideAccessibility(currentIndex = this.sliderInstance?.slider?.activeIndex ?? 0) {
        if (!FoxTheme.config.mqlTablet || !this.sliderInstance?.slider) return;

        const { slides } = this.sliderInstance.slider;
        const statusElement = this.querySelector('[id^="GalleryStatus"]');

        if (this.accessibilityInitialized && this.previousActiveIndex === currentIndex) return;
        this.accessibilityInitialized = true;
        const announceChange = this.isMovingForward === true;

        this.previousActiveIndex = currentIndex;

        slides.forEach((slide) => {
          const isActive = slide.classList.contains('swiper-slide-active');
          // The slide itself is the product__media-item
          const mediaItem = slide.classList.contains('product__media-item') ? slide : slide.querySelector('.product__media-item');
          
          if (mediaItem) {
            if (isActive) {
              // Make active slide accessible
              mediaItem.removeAttribute('aria-hidden');
              mediaItem.removeAttribute('tabindex');
              if (!mediaItem.dataset.originalRole) {
                mediaItem.dataset.originalRole = mediaItem.getAttribute('role') || '';
              }
              mediaItem.setAttribute('role', 'presentation');
              
              // Ensure images are accessible and focusable
              const images = mediaItem.querySelectorAll('img');
              images.forEach((img) => {
                // Hide underlying images from TalkBack so only the zoom button is announced
                img.setAttribute('aria-hidden', 'true');
                img.setAttribute('role', 'presentation');
                if (img.hasAttribute('tabindex')) {
                  img.removeAttribute('tabindex');
                }
                // Ensure image has alt text or aria-label
                if (!img.getAttribute('alt') && !img.getAttribute('aria-label')) {
                  const mediaIndex = parseInt(mediaItem.dataset.mediaIndex) + 1;
                  img.setAttribute('alt', `Product image ${mediaIndex}`);
                }
              });
              
              // Prefer focusing the zoom button on mobile for consistent TalkBack behavior
              const zoomButton = mediaItem.querySelector('.product__media-zoom');
              if (zoomButton) {
                zoomButton.removeAttribute('aria-hidden');
                zoomButton.setAttribute('tabindex', '0');
                zoomButton.setAttribute('data-talkback-focus', 'true');
                
                // Redirect focus from the container to the zoom button
                if (!mediaItem._mobileFocusProxy) {
                  mediaItem._mobileFocusProxy = (event) => {
                    if (document.activeElement === mediaItem) {
                      event.preventDefault();
                      zoomButton.focus();
                    }
                  };
                  mediaItem.addEventListener('focus', mediaItem._mobileFocusProxy);
                }
              }

              // Hide all other focusable elements inside the active slide
              const nonZoomFocusable = mediaItem.querySelectorAll(
                'button:not(.product__media-zoom), a:not(.product__media-zoom), input, select, textarea, [tabindex]:not(.product__media-zoom)'
              );
              nonZoomFocusable.forEach((el) => {
                el.setAttribute('aria-hidden', 'true');
                el.setAttribute('tabindex', '-1');
              });

              // If there is no zoom button, allow the media item itself to receive focus
              if (!zoomButton) {
                mediaItem.setAttribute('tabindex', '0');
              }
              
              // Hide zoom icon (decorative)
              const zoomIcon = mediaItem.querySelector('.product__media-icon');
              if (zoomIcon) {
                zoomIcon.setAttribute('aria-hidden', 'true');
              }
              
              // Hide motion-element wrappers from TalkBack to avoid blank announcements
              const motionElements = mediaItem.querySelectorAll('motion-element');
              motionElements.forEach((el) => {
                el.setAttribute('aria-hidden', 'true');
                el.setAttribute('role', 'presentation');
              });
              
              // Announce the media change - only when moving forward
              if (announceChange) {
                const mediaType = mediaItem.dataset.mediaType || 'image';
                let announcement = '';
                
                if (mediaType === 'image') {
                  const img = mediaItem.querySelector('img');
                  const altText = img ? (img.getAttribute('alt') || img.getAttribute('aria-label')) : '';
                  const mediaIndex = parseInt(mediaItem.dataset.mediaIndex) + 1;
                  announcement = altText || `Image ${mediaIndex}`;
                } else if (mediaType === 'video' || mediaType === 'external_video') {
                  const mediaIndex = parseInt(mediaItem.dataset.mediaIndex) + 1;
                  announcement = `Video ${mediaIndex}`;
                } else if (mediaType === 'model') {
                  const mediaIndex = parseInt(mediaItem.dataset.mediaIndex) + 1;
                  announcement = `3D Model ${mediaIndex}`;
                }
                
                if (announcement) {
                  // Use global announcer for reliable screen reader announcements
                  this.updateGlobalAnnouncer(announcement);
                }
              }
            } else {
              // Hide inactive slides from screen readers
              mediaItem.setAttribute('aria-hidden', 'true');
              mediaItem.setAttribute('tabindex', '-1');
              if (mediaItem.dataset.originalRole) {
                if (mediaItem.dataset.originalRole.length) {
                  mediaItem.setAttribute('role', mediaItem.dataset.originalRole);
                } else {
                  mediaItem.removeAttribute('role');
                }
              }
              
              // Hide all focusable elements inside inactive slides
              const focusableElements = mediaItem.querySelectorAll('button, a, input, select, textarea, [tabindex]');
              focusableElements.forEach(el => {
                el.setAttribute('aria-hidden', 'true');
                el.setAttribute('tabindex', '-1');
              });
              const inactiveMotionElements = mediaItem.querySelectorAll('motion-element');
              inactiveMotionElements.forEach((el) => {
                el.setAttribute('aria-hidden', 'true');
                el.setAttribute('role', 'presentation');
              });

              if (mediaItem._mobileFocusProxy) {
                mediaItem.removeEventListener('focus', mediaItem._mobileFocusProxy);
                delete mediaItem._mobileFocusProxy;
              }
            }
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      announceCurrentSlide() {
        if (!this.sliderInstance?.slider) return;

        const { slides, activeIndex } = this.sliderInstance.slider;
        const activeSlide = slides[activeIndex];
        if (!activeSlide) return;

        const mediaItem = activeSlide.classList.contains('product__media-item')
          ? activeSlide
          : activeSlide.querySelector('.product__media-item');
        if (!mediaItem) return;

        const mediaType = mediaItem.dataset.mediaType || 'image';
        const totalSlides = slides.length;
        const mediaPosition = activeIndex + 1;

        const typeLabel =
          mediaType === 'video' || mediaType === 'external_video'
            ? 'video'
            : mediaType === 'model'
            ? '3D model'
            : 'image';

        const announcement = `Current slide ${typeLabel} ${mediaPosition} of ${totalSlides} activated`;

        // Use global announcer for reliable screen reader announcements
        this.updateGlobalAnnouncer(announcement);
      }

      ensureLightboxStatusElement() {
        if (this.lightboxStatusElement && document.body.contains(this.lightboxStatusElement)) {
          return this.lightboxStatusElement;
        }

        const pswpElement = document.querySelector('.pswp');
        if (!pswpElement) return null;

        const scrollWrap = pswpElement.querySelector('.pswp__scroll-wrap');
        if (!scrollWrap) return null;

        let statusElement = scrollWrap.querySelector('.pswp__sr-status');
        if (!statusElement) {
          statusElement = document.createElement('div');
          statusElement.className = 'pswp__sr-status visually-hidden';
          statusElement.setAttribute('role', 'status');
          statusElement.setAttribute('aria-live', 'polite');
          statusElement.setAttribute('aria-atomic', 'true');
          scrollWrap.appendChild(statusElement);
        }

        this.lightboxStatusElement = statusElement;
        return statusElement;
      }

      announceLightboxSlide(force = false) {
        if (!this.lightbox?.pswp) return;

        const statusElement = this.ensureLightboxStatusElement();
        if (!statusElement) return;

        if (!force && !this.lightboxNavTriggered) return;

        const pswp = this.lightbox.pswp;
        const slide = pswp.currSlide;
        if (!slide) return;

        const mediaType = slide.data?.mediaType || 'image';
        const totalSlides =
          this.lightboxSlidesCount || this.lightbox?.options?.dataSource?.length || pswp?.getNumItems?.() || 0;
        const index = typeof pswp.currIndex === 'number' ? pswp.currIndex + 1 : 1;

        let typeLabel = 'image';
        if (mediaType === 'video' || mediaType === 'external_video') {
          typeLabel = 'video';
        } else if (mediaType === 'model') {
          typeLabel = '3D model';
        }

        const announcement = `Lightbox ${typeLabel} ${index} of ${totalSlides}`;

        // Use global announcer for reliable screen reader announcements
        this.updateGlobalAnnouncer(announcement);
      }

      updateGlobalAnnouncer(message) {
        const globalAnnouncer = document.getElementById('sr-announce');
        if (!globalAnnouncer) return;

        globalAnnouncer.textContent = '';
        setTimeout(() => {
          globalAnnouncer.textContent = message;
        }, 50);
      }

      updateNavButtonsAccessibility() {
        if (!FoxTheme.config.mqlTablet || !this.sliderInstance?.slider) return;

        const prevButton = this.querySelector('.swiper-button-prev');
        const nextButton = this.querySelector('.swiper-button-next');
        const slider = this.sliderInstance.slider;

        const setButtonState = (button, isEnabled) => {
          if (!button) return;
          if (isEnabled) {
            button.removeAttribute('aria-hidden');
            button.removeAttribute('aria-disabled');
            button.removeAttribute('disabled');
            if (button.getAttribute('tabindex') === '-1') {
              button.removeAttribute('tabindex');
            }
          } else {
            button.setAttribute('aria-hidden', 'true');
            button.setAttribute('aria-disabled', 'true');
            button.setAttribute('disabled', 'true');
            button.setAttribute('tabindex', '-1');
          }
        };

        const isBeginning = slider.isBeginning;
        const isEnd = slider.isEnd;

        setButtonState(prevButton, !isBeginning);
        setButtonState(nextButton, !isEnd);
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlTablet) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
